
import { Asset, ProjectionOptions, SimulationResult, UserInfo, WithdrawalStrategy } from "../types";

// Run Monte Carlo simulation
export function runMonteCarloSimulation(
  assets: Asset[],
  userInfo: UserInfo,
  options: ProjectionOptions,
): SimulationResult {
  const { currentAge, retirementAge, annualExpenses } = userInfo;
  const { yearsToProject, withdrawalStrategy, withdrawalRate, simulationRuns } = options;
  
  const years = Array.from({ length: yearsToProject + 1 }, (_, i) => i);
  const allSimulations: number[][] = [];
  const allWithdrawals: number[][] = [];
  let successCount = 0;
   
  // Run all simulations with proper error handling
  for (let i = 0; i < simulationRuns; i++) {
    const { portfolioProgression, portfolioEndValue, success, withdrawals } = simulateRun( 
      assets,
      userInfo,
      options
  );

    allSimulations.push(portfolioProgression);
    allWithdrawals.push(withdrawals);
    if (success) {
      successCount++
    };
  }
  
  // Calculate median portfolio value for each year
  const portfolioValues = calculateMedianValues(allSimulations);
  const withdrawalValues = calculateMedianValues(allWithdrawals);
  
  // Get ending values from all simulations
  const endingValues = allSimulations.map(sim => sim[sim.length - 1]);
  
  console.log("Withdrawals", withdrawalValues);

  return {
    years,
    portfolioValues,
    withdrawals: withdrawalValues,
    successProbability: (successCount / simulationRuns) * 100,
    medianEndingValue: calculateMedian(endingValues),
    worstCaseEndingValue: Math.min(...endingValues),
    bestCaseEndingValue: Math.max(...endingValues)
  };
}

// Calculate future value with compound interest
export function calculateFutureValue(
  assets: Asset[],
  years: number
): number[] {
  const futureValues: number[] = [];
  let totalValue = assets.reduce((sum, asset) => sum + asset.amount, 0);
  
  futureValues.push(totalValue);
  
  for (let year = 1; year <= years; year++) {
    assets.forEach(asset => {
      const interestRate = asset.interestRate / 100;
      asset.amount = asset.amount * (1 + interestRate);
    });
    
    totalValue = assets.reduce((sum, asset) => sum + asset.amount, 0);
    futureValues.push(totalValue);
  }
  
  return futureValues;
}

// Simulate a single Monte Carlo run
function simulateRun(
  assets: Asset[],
  userInfo: UserInfo,
  options: ProjectionOptions,
): { portfolioProgression: number[], success: boolean, portfolioEndValue: number, withdrawals: number[] } {
  const { currentAge, retirementAge, annualExpenses } = userInfo;
  const { yearsToProject, withdrawalStrategy, withdrawalRate, simulationRuns } = options;
  const years = Array.from({ length: yearsToProject + 1 }, (_, i) => i);  
  const assetsCopy: Asset[] = JSON.parse(JSON.stringify(assets));
  const assetReturns = assetsCopy.map(asset => {
    return simulateReturns(asset.interestRate / 100, asset.volatility / 100, yearsToProject);
  });


  let portfolioValue = assetsCopy.reduce((sum, asset) => sum + asset.amount, 0);
  
  const portfolioValues: number[] = [];
  const withdrawals: number[] = [];
  
  // Calculate total asset weights for proportional withdrawals
  let assetWeights = assetsCopy.map(asset => asset.amount / portfolioValue);
  
  // Years until retirement
  const yearsToRetirement = retirementAge - currentAge;
  
  // For each projected year
  for (let year = 0; year < yearsToProject; year++) {
    // Step 1: Apply returns to each asset for the current year
    for (let i = 0; i < assetsCopy.length; i++) {
      const asset = assetsCopy[i];
      const annualReturn = assetReturns[i][year];
      
      // Apply the return for this year to the asset amount
      asset.amount *= Math.exp(annualReturn);
    }

    portfolioValue = assetsCopy.reduce((sum, asset) => sum + asset.amount, 0);
    
    // Step 2: Add annual contributions if before retirement
    if (year < yearsToRetirement) {
      for (let i = 0; i < assetsCopy.length; i++) {
        if (assetsCopy[i].annualContribution > 0) {
          assetsCopy[i].amount += assetsCopy[i].annualContribution;
        }
      }
      // Update portfolio value after contributions
      portfolioValue = assetsCopy.reduce((sum, asset) => sum + asset.amount, 0);
    }
    
    let withdrawalAmount = 0;

    // Step 3: Handle withdrawals if in retirement
    if (year >= yearsToRetirement) {
      
      
      // Determine withdrawal amount based on strategy
      if (withdrawalStrategy === WithdrawalStrategy.CONSTANT_PERCENTAGE) {
        // Simple percentage-based withdrawal
        withdrawalAmount = portfolioValue * (withdrawalRate / 100);
      } else if (withdrawalStrategy === WithdrawalStrategy.GUARDRAILS) {
        // Guardrails strategy - adjust based on portfolio performance
        // Base withdrawal amount
        const baseWithdrawal = portfolioValue * (withdrawalRate / 100);
        
        // Check if we need to adjust the withdrawal rate based on performance
        // For simplicity, I'm implementing a basic guardrails approach:
        // If portfolio has grown significantly, increase withdrawal
        // If portfolio has shrunk significantly, decrease withdrawal
        if (year > yearsToRetirement) {
          const previousPortfolioValue = portfolioValues[portfolioValues.length - 1];
          const portfolioGrowthRate = portfolioValue / previousPortfolioValue - 1;
          
          if (portfolioGrowthRate > 0.2) {
            // Portfolio grew by more than 20%, increase withdrawal by 10%
            withdrawalAmount = baseWithdrawal * 1.1;
          } else if (portfolioGrowthRate < -0.1) {
            // Portfolio shrunk by more than 10%, decrease withdrawal by 10%
            withdrawalAmount = baseWithdrawal * 0.9;
          } else {
            withdrawalAmount = baseWithdrawal;
          }
        } else {
          withdrawalAmount = baseWithdrawal;
        }
      }
      
      // Ensure we don't withdraw more than what's available
      withdrawalAmount = Math.min(withdrawalAmount, portfolioValue);
      
      // Skip withdrawal if portfolio is depleted
      if (portfolioValue <= 0) {
        withdrawalAmount = 0;
      }
      
      // Withdraw proportionally from each asset
      if (withdrawalAmount > 0) {
        // Recalculate asset weights based on current values
        assetWeights = assetsCopy.map(asset => asset.amount / portfolioValue);
        
        for (let i = 0; i < assetsCopy.length; i++) {
          const assetWithdrawal = withdrawalAmount * assetWeights[i];
          assetsCopy[i].amount -= assetWithdrawal;
        }
        
        // Update portfolio value after withdrawal
        portfolioValue = assetsCopy.reduce((sum, asset) => sum + asset.amount, 0);
      }

    }
    
    // Record portfolio value for this year
    portfolioValues.push(portfolioValue);
    withdrawals.push(withdrawalAmount);
    
    // Check for simulation failure (ran out of money)
    if (portfolioValue <= 5000) {
      // Fill remaining years with zero
      for (let i = portfolioValues.length; i <= yearsToProject; i++) {
        portfolioValues.push(0);
        withdrawals.push(0);
      }
      break;
    }
  }
  
  return { portfolioEndValue: portfolioValue, success: portfolioValue > 0, portfolioProgression: portfolioValues, withdrawals };
}

// Generate a random return for an asset following geometric brownian motion
function generateRandomReturn(mean: number, stdDev: number): number {
  const randomShock = Math.random() * stdDev;
  const plusOrMinus = Math.random() < 0.5 ? -1 : 1;
  return mean + plusOrMinus * randomShock;
}

// Calculate withdrawal amount based on strategy
function calculateWithdrawalAmount(
  portfolioValue: number,
  strategy: WithdrawalStrategy,
  withdrawalRate: number,
  annualExpenses: number,
  initialWithdrawal: number = 0
): number {
  const rate = withdrawalRate / 100;
  
  switch (strategy) {
     
    case WithdrawalStrategy.CONSTANT_PERCENTAGE:
      // Constant percentage of current portfolio
      return portfolioValue * rate;

      
    case WithdrawalStrategy.GUARDRAILS:
      // Guardrails approach (simplified)
      const baseWithdrawal = initialWithdrawal || (portfolioValue * rate);
      
      // If portfolio has grown significantly, increase withdrawal
      if (portfolioValue * rate > baseWithdrawal * 1.2) {
        return baseWithdrawal * 1.1;
      }
      // If portfolio has shrunk significantly, reduce withdrawal
      else if (portfolioValue * rate < baseWithdrawal * 0.8) {
        return baseWithdrawal * 0.9;
      }
      // Otherwise maintain the same withdrawal
      return baseWithdrawal;
      
    default:
      return annualExpenses;
  }
}



// Calculate median values for each year across all simulations
function calculateMedianValues(simulations: number[][]): number[] {
  const numYears = simulations[0].length;
  const result: number[] = [];
  
  for (let year = 0; year < numYears; year++) {
    const valuesForYear = simulations.map(sim => sim[year]);
    result.push(calculateMedian(valuesForYear));
  }
  
  return result;
}

// Calculate median of an array of numbers
function calculateMedian(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  
  return sorted[middle];
}

/**
 * Simulates annual returns using Geometric Brownian Motion for a specified number of years
 * 
 * @param {number} meanReturn - Mean annual return
 * @param {number} volatility - Annual volatility (standard deviation)
 * @param {number} years - Number of years to simulate (default: 25)
 * @returns {number[]} Array of simulated annual returns
 */
function simulateReturns(meanReturn: number, volatility: number, years: number = 25): number[] {
  // Validate inputs
  if (years <= 0) {
    throw new Error("Number of years must be positive");
  }
  if (volatility < 0) {
    throw new Error("Volatility must be non-negative");
  }
  
  // Initialize array for annual returns
  const annualReturns: number[] = [];
  
  // Generate the annual returns directly (dt = 1 for annual)
  for (let i = 0; i < years; i++) {
    // Generate random normal variable (Box-Muller transform)
    const u1 = Math.random();
    const u2 = Math.random();
    const z = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
    
    // Calculate annual return using GBM
    // The drift component accounts for volatility drag
    const drift = meanReturn - 0.5 * volatility * volatility;
    const diffusion = volatility * z;
    
    // The full annual return (as a decimal, e.g., 0.07 for 7%)
    const annualReturn = drift + diffusion;
    annualReturns.push(annualReturn);
  }
  
  return annualReturns;
}